import React, { useState } from 'react';
import styled from 'styled-components';
import { FiX, FiLayers, FiCheck, FiArrowRight, FiArrowLeft, FiTarget, FiPlus, FiTrash2 } from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';
import { useAssetClass } from '../../contexts/AssetClassContext';
import toast from 'react-hot-toast';
import { TOKENIZER_PLATFORM_SECTORS } from '../../api/smartTokenizerMappers';

const WizardContainer = styled.div`
  padding: 2rem;
  height: 100%;
  overflow-y: auto;
  
  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.05);
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(79, 172, 254, 0.3);
    border-radius: 3px;
    
    &:hover {
      background: rgba(79, 172, 254, 0.5);
    }
  }

  @media (max-width: 768px) {
    padding: 1.5rem;
  }
`;

const WizardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const WizardTitle = styled.h2`
  color: #fff;
  font-size: 1.8rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 1rem;

  .icon {
    color: #4facfe;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: #a0a0a0;
  font-size: 1.5rem;
  cursor: pointer;
  transition: color 0.3s ease;

  &:hover {
    color: #fff;
  }
`;

const StepContent = styled(motion.div)`
  min-height: 400px;
  margin-bottom: 2rem;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FormSection = styled.div`
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 1.5rem;

  .section-title {
    font-size: 1.1rem;
    font-weight: 600;
    color: #fff;
    margin-bottom: 1rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;

    .icon {
      color: #4facfe;
    }
  }
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: ${props => props.columns || '1fr'};
  gap: 1rem;
  margin-bottom: 1rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;

  label {
    color: #fff;
    font-weight: 500;
    margin-bottom: 0.5rem;
    font-size: 0.9rem;
  }

  input, select, textarea {
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 8px;
    padding: 0.75rem;
    color: #fff;
    font-size: 0.9rem;
    transition: all 0.3s ease;

    &:focus {
      border-color: #4facfe;
      box-shadow: 0 0 0 3px rgba(79, 172, 254, 0.1);
    }

    &::placeholder {
      color: #a0a0a0;
    }
  }

  select {
    option {
      background: #1a1a3e;
      color: #fff;
    }
  }

  textarea {
    min-height: 100px;
    resize: vertical;
  }

  .field-hint {
    font-size: 0.8rem;
    color: #a0a0a0;
    margin-top: 0.25rem;
  }

  .field-error {
    font-size: 0.8rem;
    color: #ff6b6b;
    margin-top: 0.25rem;
  }
`;

const SchemaBuilder = styled.div`
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 1.5rem;

  .schema-title {
    font-size: 1.1rem;
    font-weight: 600;
    color: #fff;
    margin-bottom: 1rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;

    .icon {
      color: #4facfe;
    }
  }

  .schema-fields {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .field-item {
    display: flex;
    align-items: center;
    gap: 1rem;
    padding: 1rem;
    background: rgba(255, 255, 255, 0.02);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 8px;

    .field-name {
      flex: 1;
    }

    .field-type {
      width: 120px;
    }

    .field-required {
      width: 80px;
    }

    .field-actions {
      width: 40px;
      display: flex;
      justify-content: center;
    }

    .remove-field {
      background: rgba(255, 107, 107, 0.1);
      border: 1px solid rgba(255, 107, 107, 0.3);
      border-radius: 6px;
      padding: 0.5rem;
      color: #ff6b6b;
      cursor: pointer;
      transition: all 0.3s ease;

      &:hover {
        background: rgba(255, 107, 107, 0.2);
      }
    }
  }

  .add-field {
    padding: 0.75rem 1rem;
    background: rgba(79, 172, 254, 0.1);
    border: 1px dashed rgba(79, 172, 254, 0.3);
    border-radius: 8px;
    color: #4facfe;
    cursor: pointer;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;

    &:hover {
      background: rgba(79, 172, 254, 0.2);
    }
  }
`;

const ReviewSection = styled.div`
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 2rem;

  .review-title {
    font-size: 1.2rem;
    font-weight: 600;
    color: #fff;
    margin-bottom: 1.5rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;

    .icon {
      color: #4facfe;
    }
  }

  .review-section {
    margin-bottom: 2rem;

    .section-header {
      font-weight: 600;
      color: #4facfe;
      margin-bottom: 1rem;
      font-size: 1rem;
    }

    .review-item {
      display: flex;
      justify-content: space-between;
      padding: 0.5rem 0;
      border-bottom: 1px solid rgba(255, 255, 255, 0.05);

      &:last-child {
        border-bottom: none;
      }

      .item-label {
        color: #a0a0a0;
        font-weight: 500;
      }

      .item-value {
        color: #fff;
        font-weight: 600;
      }
    }

    .schema-preview {
      background: rgba(79, 172, 254, 0.05);
      border: 1px solid rgba(79, 172, 254, 0.2);
      border-radius: 8px;
      padding: 1rem;

      .field-list {
        display: flex;
        flex-wrap: wrap;
        gap: 0.5rem;
      }

      .field-tag {
        background: rgba(79, 172, 254, 0.1);
        border: 1px solid rgba(79, 172, 254, 0.3);
        border-radius: 4px;
        padding: 0.25rem 0.5rem;
        font-size: 0.8rem;
        color: #4facfe;
      }
    }
  }
`;

const WizardActions = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  padding-top: 1rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
`;

const ActionButton = styled.button`
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  ${props => props.variant === 'primary' ? `
    background: linear-gradient(135deg, #4facfe, #00f2fe);
    color: #fff;
    border: none;
    
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 5px 15px rgba(79, 172, 254, 0.3);
    }
    
    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
      transform: none;
      box-shadow: none;
    }
  ` : `
    background: rgba(255, 255, 255, 0.1);
    color: #fff;
    border: 1px solid rgba(255, 255, 255, 0.2);
    
    &:hover {
      background: rgba(255, 255, 255, 0.2);
    }
  `}
`;

function AssetClassCreationWizard({ onClose, platformStandard = false }) {
  const { createAssetClass, ASSET_CLASS_TYPES, loading } = useAssetClass();
  
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    name: '',
    category: '',
    classType: 'standardizedSeries',
    marketSymbol: '',
    tradingUnit: '',
    description: '',
    schemaFields: [
      { name: 'serialNumber', type: 'string', required: true, id: 1 }
    ]
  });
  const [validationErrors, setValidationErrors] = useState({});

  const updateFormData = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const addSchemaField = () => {
    const newField = {
      id: Date.now(),
      name: '',
      type: 'string',
      required: false
    };
    
    setFormData(prev => ({
      ...prev,
      schemaFields: [...prev.schemaFields, newField]
    }));
  };

  const updateSchemaField = (id, field, value) => {
    setFormData(prev => ({
      ...prev,
      schemaFields: prev.schemaFields.map(f => 
        f.id === id ? { ...f, [field]: value } : f
      )
    }));
  };

  const removeSchemaField = (id) => {
    setFormData(prev => ({
      ...prev,
      schemaFields: prev.schemaFields.filter(f => f.id !== id)
    }));
  };

  const validateCurrentStep = () => {
    const errors = {};
    
    switch (currentStep) {
      case 1:
        if (!formData.name) errors.name = 'Name is required';
        if (!formData.category) errors.category = 'Category is required';
        if (!formData.marketSymbol) errors.marketSymbol = 'Market symbol is required';
        if (!formData.tradingUnit) errors.tradingUnit = 'Trading unit is required';
        break;
      
      case 2:
        const emptyFields = formData.schemaFields.filter(f => !f.name);
        if (emptyFields.length > 0) {
          errors.schema = 'All schema fields must have names';
        }
        break;
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const nextStep = () => {
    if (validateCurrentStep()) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => prev - 1);
  };

  const handleSubmit = async () => {
    if (!validateCurrentStep()) return;
    
    try {
      const standardSchema = {
        required: formData.schemaFields.filter(f => f.required).map(f => f.name),
        specifications: {}
      };
      
      formData.schemaFields.forEach(field => {
        standardSchema.specifications[field.name] = {
          type: field.type
        };
      });
      
      const classData = {
        name: formData.name,
        category: formData.category,
        classType: formData.classType,
        marketSymbol: formData.marketSymbol,
        tradingUnit: formData.tradingUnit,
        description: formData.description,
        standardSchema
      };
      
      await createAssetClass(classData, { platformStandard });
      onClose();
      
    } catch (error) {
      toast.error('Failed to create asset class');
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <StepContent
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
          >
            <Form>
              <FormSection>
                <div className="section-title">
                  <FiTarget className="icon" />
                  Basic Information
                </div>
                
                <FormRow columns="1fr 1fr">
                  <FormGroup>
                    <label>Asset Class Name</label>
                    <input
                      type="text"
                      placeholder="e.g., Premium Gold Bars"
                      value={formData.name}
                      onChange={(e) => updateFormData('name', e.target.value)}
                    />
                    {validationErrors.name && (
                      <div className="field-error">{validationErrors.name}</div>
                    )}
                  </FormGroup>
                  
                  <FormGroup>
                    <label>Category</label>
                    <select
                      value={formData.category}
                      onChange={(e) => updateFormData('category', e.target.value)}
                    >
                      <option value="">Select category</option>
                      {TOKENIZER_PLATFORM_SECTORS.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.name}
                        </option>
                      ))}
                    </select>
                    {validationErrors.category && (
                      <div className="field-error">{validationErrors.category}</div>
                    )}
                  </FormGroup>
                </FormRow>
                
                <FormGroup>
                  <label>Class Type</label>
                  <select
                    value={formData.classType}
                    onChange={(e) => updateFormData('classType', e.target.value)}
                  >
                    {Object.values(ASSET_CLASS_TYPES).map(type => (
                      <option key={type.id} value={type.id}>
                        {type.name} - {type.description}
                      </option>
                    ))}
                  </select>
                </FormGroup>
                
                <FormRow columns="1fr 1fr">
                  <FormGroup>
                    <label>Market Symbol</label>
                    <input
                      type="text"
                      placeholder="e.g., GOLD"
                      value={formData.marketSymbol}
                      onChange={(e) => updateFormData('marketSymbol', e.target.value.toUpperCase())}
                    />
                    {validationErrors.marketSymbol && (
                      <div className="field-error">{validationErrors.marketSymbol}</div>
                    )}
                  </FormGroup>
                  
                  <FormGroup>
                    <label>Trading Unit</label>
                    <input
                      type="text"
                      placeholder="e.g., oz, kg, sqft"
                      value={formData.tradingUnit}
                      onChange={(e) => updateFormData('tradingUnit', e.target.value)}
                    />
                    {validationErrors.tradingUnit && (
                      <div className="field-error">{validationErrors.tradingUnit}</div>
                    )}
                  </FormGroup>
                </FormRow>
                
                <FormGroup>
                  <label>Description</label>
                  <textarea
                    placeholder="Describe this asset class..."
                    value={formData.description}
                    onChange={(e) => updateFormData('description', e.target.value)}
                  />
                </FormGroup>
              </FormSection>
            </Form>
          </StepContent>
        );

      case 2:
        return (
          <StepContent
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
          >
            <SchemaBuilder>
              <div className="schema-title">
                <FiLayers className="icon" />
                Metadata Schema Definition
              </div>
              
              <div className="schema-fields">
                {formData.schemaFields.map(field => (
                  <div key={field.id} className="field-item">
                    <FormGroup className="field-name">
                      <input
                        type="text"
                        placeholder="Field name"
                        value={field.name}
                        onChange={(e) => updateSchemaField(field.id, 'name', e.target.value)}
                      />
                    </FormGroup>
                    
                    <FormGroup className="field-type">
                      <select
                        value={field.type}
                        onChange={(e) => updateSchemaField(field.id, 'type', e.target.value)}
                      >
                        <option value="string">String</option>
                        <option value="number">Number</option>
                        <option value="integer">Integer</option>
                        <option value="boolean">Boolean</option>
                        <option value="date">Date</option>
                      </select>
                    </FormGroup>
                    
                    <FormGroup className="field-required">
                      <label>
                        <input
                          type="checkbox"
                          checked={field.required}
                          onChange={(e) => updateSchemaField(field.id, 'required', e.target.checked)}
                        />
                        Required
                      </label>
                    </FormGroup>
                    
                    <div className="field-actions">
                      {formData.schemaFields.length > 1 && (
                        <button
                          type="button"
                          className="remove-field"
                          onClick={() => removeSchemaField(field.id)}
                        >
                          <FiTrash2 />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
                
                <button
                  type="button"
                  className="add-field"
                  onClick={addSchemaField}
                >
                  <FiPlus />
                  Add Field
                </button>
              </div>
              
              {validationErrors.schema && (
                <div className="field-error">{validationErrors.schema}</div>
              )}
            </SchemaBuilder>
          </StepContent>
        );

      case 3:
        return (
          <StepContent
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
          >
            <ReviewSection>
              <div className="review-title">
                <FiCheck className="icon" />
                Review Asset Class
              </div>
              
              <div className="review-section">
                <div className="section-header">Basic Information</div>
                <div className="review-item">
                  <span className="item-label">Name</span>
                  <span className="item-value">{formData.name}</span>
                </div>
                <div className="review-item">
                  <span className="item-label">Category</span>
                  <span className="item-value">{formData.category}</span>
                </div>
                <div className="review-item">
                  <span className="item-label">Class Type</span>
                  <span className="item-value">
                    {ASSET_CLASS_TYPES[formData.classType]?.name}
                  </span>
                </div>
                <div className="review-item">
                  <span className="item-label">Market Symbol</span>
                  <span className="item-value">{formData.marketSymbol}</span>
                </div>
                <div className="review-item">
                  <span className="item-label">Trading Unit</span>
                  <span className="item-value">{formData.tradingUnit}</span>
                </div>
              </div>
              
              <div className="review-section">
                <div className="section-header">Metadata Schema</div>
                <div className="schema-preview">
                  <div className="field-list">
                    {formData.schemaFields.map(field => (
                      <span key={field.id} className="field-tag">
                        {field.name} ({field.type})
                        {field.required && ' *'}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </ReviewSection>
          </StepContent>
        );

      default:
        return null;
    }
  };

  return (
    <WizardContainer>
      <WizardHeader>
        <WizardTitle>
          <FiLayers className="icon" />
          Create Custom Asset Class
        </WizardTitle>
        <CloseButton onClick={onClose}>
          <FiX />
        </CloseButton>
      </WizardHeader>

      <AnimatePresence mode="wait">
        {renderStepContent()}
      </AnimatePresence>

      <WizardActions>
        <ActionButton
          variant="secondary"
          onClick={prevStep}
          disabled={currentStep === 1}
        >
          <FiArrowLeft />
          Previous
        </ActionButton>
        
        {currentStep < 3 ? (
          <ActionButton
            variant="primary"
            onClick={nextStep}
          >
            Next
            <FiArrowRight />
          </ActionButton>
        ) : (
          <ActionButton
            variant="primary"
            onClick={handleSubmit}
            disabled={loading}
          >
            <FiCheck />
            Create Asset Class
          </ActionButton>
        )}
      </WizardActions>
    </WizardContainer>
  );
}

export default AssetClassCreationWizard; 